import React from 'react';
import { CheckboxWithLabel } from '@noths/polaris-client-ribbons-design-system';
import { visuallyHidden } from '@noths/polaris-client-styles';

import * as styles from './styles';

type CheckboxData = {
  checked?: boolean;
  value: string;
  labelText: string;
  secondaryLabelText?: string;
};

export interface CheckboxGroupProps {
  accessibleLabel: string;
  accessibleLabelElementType?: keyof JSX.IntrinsicElements;
  accessibleLabelElementId?: string;
  name: string;
  onChange: (ev: React.ChangeEvent<HTMLInputElement>) => void;
  checkboxData: CheckboxData[];
  hideLabel?: boolean;
}

/**
 * The accessibleLabelElementType prop should be a string which represents the type of HTML element to use for the label e.g. 'h1'.
 * The accessibleLabelElementId prop should contain a string to customise the id attribute for the label element containing the accessible label.
 */
export const CheckboxGroup = ({
  name,
  accessibleLabel,
  accessibleLabelElementType: AccessibleLabelElementTag = 'p',
  accessibleLabelElementId = `${name}-label`,
  onChange,
  checkboxData,
  hideLabel,
}: CheckboxGroupProps) => {
  return (
    <div role="group" aria-labelledby={accessibleLabelElementId}>
      <span id={accessibleLabelElementId} css={hideLabel ? visuallyHidden : undefined}>
        <AccessibleLabelElementTag>{accessibleLabel}</AccessibleLabelElementTag>
      </span>
      {checkboxData.map(({ checked, labelText, value: checkboxValue, secondaryLabelText }) => (
        <div key={checkboxValue} css={styles.checkboxWrapperContainer} data-testid={checkboxValue}>
          <CheckboxWithLabel
            /*
            Fixes https://notonthehighstreet.atlassian.net/browse/FFA-2190.
            Transform scale works by moving the element outside its actual layer hierarchy
            before manipulating it, which is the likely culprit behind the checkmark icon
            not being shown following a reflow/repaint in complex layout changes.
          */
            disableCssTransforms
            css={styles.checkboxWrapper}
            name={name}
            label={labelText}
            value={checkboxValue}
            key={checkboxValue}
            checked={checked}
            onChange={onChange}
          />
          {secondaryLabelText && <span css={styles.secondaryLabel}>{secondaryLabelText}</span>}
        </div>
      ))}
    </div>
  );
};
